import React from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {ComplianceStandardResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetGlobalComplianceStandardListV2Query} from 'src/redux/queries/complianceStandard';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {
  complianceStandardDocumentTitle,
  pageTitleDict,
} from '../complianceStandardHelpers';
import {useComplianceStandardColumns} from './hooks/useComplianceStandardColumns';

import {SidebarLinkComplianceStandards} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const ComplianceStandardListPage: React.FC = () => {
  useSetDocumentTitle(complianceStandardDocumentTitle);
  const navigate = useAppNavigate();

  const columns = useComplianceStandardColumns();

  const tableState = useTableState({
    identifier: 'compliance-standard-list-page',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'titleNumberLabel', desc: false}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetGlobalComplianceStandardListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleNavigateToStandard = (row: ComplianceStandardResponse) =>
    navigate(PathUtility.ComplianceStandardsView(row.id));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),

      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateToStandard(row.original)}
          />
        </TableRowActions>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkComplianceStandards.icon}
          subtitle="View regulatory compliance standards">
          {pageTitleDict.plural}
        </PageHeader.Title>
      </PageHeader>
      {table}
    </ContentWrapper>
  );
};

export default ComplianceStandardListPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ComplianceStandards(),
  element: <ComplianceStandardListPage />,
  requireSuperAdmin: true,
  isPrivate: true,
});
